import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookUser, FileUp, Check, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { canPickContacts, pickFromPhone, parseVcf, toCountry, dropKnown, matchKeys } from "@/lib/phonebook";
import { Avatar } from "@/components/friends/Avatar";
import { PhoneNudge } from "@/components/contacts/PhoneNudge";
import { InviteRest } from "@/components/contacts/InviteRest";
import { CityInput } from "@/components/contacts/CityInput";
import { CountryPicker } from "@/components/travel/CountryPicker";
import { PrivateNote } from "@/components/contacts/PrivateNote";
import { Button } from "@/components/ui/button";

// Someone in your phone book already on the bucketlist: one tap sends a friend request
const OnApp = ({ m, i, onSent }) => {
  const send = async () => {
    try { onSent((await api.sendRequest(m.user_id)).relation); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the request"); }
  };
  return (
    <div className="flex items-center gap-2 pl-7 text-xs" data-testid={`import-onapp-${i}`}>
      <Avatar f={m} size="w-6 h-6" />
      <span className="flex-1 min-w-0 truncate text-sky-300">{m.name} is on the bucketlist</span>
      {m.relation === "friend" ? <span className="text-emerald-300" data-testid={`import-friend-state-${i}`}>Friends</span>
        : m.relation === "sent" ? <span className="text-slate-400" data-testid={`import-friend-state-${i}`}>Request sent</span>
        : <button type="button" onClick={send} className="flex items-center gap-1 rounded-full bg-sky-400/15 px-2.5 py-1 text-sky-200 hover:bg-sky-400/25 transition-colors" data-testid={`import-add-friend-${i}`}><UserPlus size={12} /> {m.relation === "received" ? "Accept" : "Add friend"}</button>}
    </div>
  );
};

const Row = ({ p, i, names, set, warn, m, onSent }) => (
  <li className={`py-3 px-2 -mx-2 rounded-lg border-b border-white/5 space-y-2 transition-opacity ${p.on ? "" : "opacity-50"} ${warn ? "ring-1 ring-rose-400/70 bg-rose-400/5" : ""}`} data-testid={`import-row-${i}`} data-missing={warn ? "true" : undefined}>
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={p.on} onChange={(e) => set({ on: e.target.checked })} className="h-4 w-4 accent-emerald-400" data-testid={`import-check-${i}`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium truncate" data-testid={`import-name-${i}`}>{p.name || p.phone || p.email}</span>
        <span className="block text-xs text-slate-500 truncate">{[p.phone, p.email].filter(Boolean).join(" · ") || "No phone or email"}</span>
      </span>
    </label>
    {m && <OnApp m={m} i={i} onSent={onSent} />}
    {p.on && (
      <div className="flex items-center gap-2 pl-7">
        <CountryPicker names={names} exclude={[]} onPick={(c) => set({ country: c })} label={p.country || "Pick country"} testId={`import-country-${i}`}
          className={p.country ? "bg-amber-400/15 text-amber-200 hover:bg-amber-400/25 border border-amber-400/40" : "bg-white/10 text-slate-100 hover:bg-white/15"} />
        <CityInput country={p.country} value={p.city} onChange={(city) => set({ city })} className="flex-1" testId={`import-city-${i}`} />
      </div>
    )}
  </li>
);

const HowTo = () => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-400 space-y-1.5" data-testid="import-iphone-help">
    <p className="text-slate-200 font-medium">On iPhone</p>
    <p>Open the <b>Contacts</b> app, pick a person (or tap Lists, pick a list and choose Export), then <b>Share → Save to Files</b>. Come back and tap <b>Choose contacts file</b>.</p>
    <p>Or turn on <b>Settings → Apps → Safari → Advanced → Feature Flags → Contact Picker API</b> and reload this page to tick people straight from your phone book.</p>
  </div>
);

export const PhoneBookImport = ({ names, onDone }) => {
  const qc = useQueryClient();
  const file = useRef(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [skipped, setSkipped] = useState(0);
  const [onApp, setOnApp] = useState({});
  const [saved, setSaved] = useState(null);
  const picker = canPickContacts();
  const matchOf = (p) => matchKeys(p).map((k) => onApp[k]).find(Boolean);
  const setRel = (m, relation) => setOnApp(Object.fromEntries(Object.entries(onApp).map(([k, v]) => [k, v.user_id === m.user_id ? { ...v, relation } : v])));
  const load = async (list) => {
    if (!list.length) return toast.error("No contacts found");
    const { fresh, skipped: n } = dropKnown(list, qc.getQueryData(["state"])?.wishlist);
    setTried(false); setSkipped(n); setOnApp({}); setSaved(null);
    api.watchPeople({ people: list.slice(0, 500).map(({ name, email, phone }) => ({ name, email, phone })) }).catch(() => null);
    setRows(fresh.slice(0, 300).map((p) => ({ ...p, on: true, country: toCountry(p.country, names) })));
    if (!fresh.length) return;
    try {
      const found = await api.matchPeople({ emails: fresh.map((p) => p.email).filter(Boolean).slice(0, 500), phones: fresh.map((p) => p.phone).filter(Boolean).slice(0, 500) });
      setOnApp(Object.fromEntries(found.flatMap((m) => m.keys.map((k) => [k, m]))));
    } catch { /* matching is a bonus; the import still works */ }
  };
  const pick = async () => {
    try { load(await pickFromPhone()); } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open your phone book"); }
  };
  const readFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) load(parseVcf(await f.text()));
  };
  const chosen = rows.filter((r) => r.on);
  const save = async () => {
    if (chosen.some((r) => !r.country)) {
      setTried(true);
      setTimeout(() => document.querySelector('[data-missing="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      return toast.error("Pick a country for everyone you ticked — they're marked in red");
    }
    setBusy(true);
    let ok = 0;
    const failed = [];
    const done = [];
    for (const r of chosen) {
      try {
        const res = await api.addContact({ country: r.country, city: r.city, contact: { name: r.name, phone: r.phone, email: r.email } });
        ok++;
        if (!matchOf(r) && (r.phone || r.email)) done.push({ ...r, wish_id: res.wish_id });
      } catch { failed.push(r); }
    }
    qc.invalidateQueries({ queryKey: ["state"] });
    setBusy(false);
    if (ok && !(done.length && !failed.length)) toast.success(`${ok} travel friend${ok === 1 ? "" : "s"} saved`);
    if (failed.length) { toast.error(`${failed.length} couldn't be saved`); setRows(failed); } else { setRows([]); if (done.length) setSaved(done); else onDone(); }
  };
  return (
    <div className="mt-4 space-y-4" data-testid="phonebook-import">
      <PhoneNudge testId="import-phone-nudge" />
      <p className="text-sm text-slate-300">Only the people you tick are saved. We keep a private, scrambled note of everyone you pick so we can tell you if they join later.</p>
      <div className="flex flex-wrap gap-2">
        {picker && <Button onClick={pick} className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="import-pick-phone"><BookUser size={15} /> Pick from phone book</Button>}
        <Button variant="outline" onClick={() => file.current?.click()} className="border-white/15 bg-transparent" data-testid="import-pick-file"><FileUp size={15} /> Choose contacts file</Button>
        <input ref={file} type="file" accept=".vcf,text/vcard,text/x-vcard" onChange={readFile} className="hidden" data-testid="import-file-input" />
      </div>
      {skipped > 0 && <p className="text-xs text-slate-400" data-testid="import-skipped">{skipped} {skipped === 1 ? "person was" : "people were"} left out — already in your travel friends or listed twice.</p>}
      {saved && <InviteRest people={saved} savedCount={chosen.length} onDone={() => { setSaved(null); onDone(); }} />}
      {!picker && !rows.length && !saved && <HowTo />}
      {rows.length > 0 && (
        <>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span data-testid="import-count">{chosen.length} of {rows.length} ticked</span>
            <button onClick={() => setRows(rows.map((r) => ({ ...r, on: chosen.length !== rows.length })))} className="text-emerald-300 hover:text-emerald-200" data-testid="import-toggle-all">{chosen.length === rows.length ? "Untick all" : "Tick all"}</button>
          </div>
          <ul data-testid="import-list">{rows.map((p, i) => <Row key={i} p={p} i={i} names={names} warn={tried && p.on && !p.country} m={matchOf(p)} onSent={(r) => setRel(matchOf(p), r)} set={(v) => setRows(rows.map((r, j) => (j === i ? { ...r, ...v } : r)))} />)}</ul>
          <Button onClick={save} disabled={busy || !chosen.length} className="w-full bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="import-save"><Check size={15} /> {busy ? "Saving…" : `Save ${chosen.length} travel friend${chosen.length === 1 ? "" : "s"}`}</Button>
        </>
      )}
      <PrivateNote testId="import-private-note" />
    </div>
  );
};
